import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { nanoid } from 'nanoid';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import type { CalculatePriceRequestBody, TransactionItem } from '@/api/forex';
import { useCalculatePrice, useCurrencyRates } from '@/api/forex';
import AddItemIcon from '@/assets/images/icons/add-item.svg';
import CloseIcon from '@/assets/images/icons/close-icon.svg';
import DeleteIcon from '@/assets/images/icons/delete-icon.svg';
import EditIcon from '@/assets/images/icons/edit-icon.svg';
import DangerIcon from '@/assets/images/icons/errorIcon.png';
import WarningIcon from '@/assets/images/icons/warningIcon.png';
import type { LeadStore } from '@/common/components/Forex/components/landing-page/PreLogin';
import { useNotificationStore } from '@/common/store';
import routes from '@/routes/routes.json';
import useMoengageStore from '@/store/moengage';
import { createMoengageEvent } from '@/utils/moengage';

import BasicDropdown from '../BasicDropdown';
import { FieldWrapper } from '../Form';
import CurrencyIcon from './components/reusables/CurrencyIcon';
import type { Currency } from './CurrencyDropdown';
import CurrencyDropdown from './CurrencyDropdown';
import { FlowType, TransactionProcess } from './enums';
import { useForexStore } from './store/store';

export type CalcFormType = {
  forexAmount: string;
  inrAmount: string;
  exchangeRate: number;
  selectedCurrency: string;
  transactionType: string;
  exchangeTime: string;
  transactionProcess: TransactionProcess.Online | TransactionProcess.Branch;
  isSupportedByOmFx: boolean;
};

export type EditCalcFormType = {
  forexAmount: string;
  inrAmount: string;
  exchangeRate: number;
  selectedCurrency: string;
  transactionType: string;
  exchangeTime: string;
  id?: string;
  isSupportedByOmFx: boolean;
};

const CalcFormSchema: any = yup.object({
  forexAmount: yup
    .number()
    .typeError('Forex Currency must be a number')
    .min(1, 'Minimum Forex Currency is 1')
    .max(1000000, 'Max Forex Currency is 10,00,000')
    .required('Forex Currency is required'),
  inrAmount: yup
    .number()
    .typeError('INR Currency must be a number')
    .required('Forex Currency is required'),
  selectedCurrency: yup.string().required('Currency is required'),
  transactionType: yup.string().required('Transaction Type is required'),
});

const buyOptionValues: { [key: string]: string } = {
  'Forex Card': 'BUY_CARD',
  'Forex Cash': 'BUY_CASH',
};

export const buyOptions = Object.keys(buyOptionValues);

export type Item = {
  id: string;
  transactionType: string;
  currency: string;
  inrAmount: string;
  forexAmount: string;
  exchangeRate: number;
  exchangeTime: string;
  isSupportedByOmFx: boolean;
};

export type TransactionDetails = {
  transactionType: string;
  currency: string;
  exchangeRate: number;
  totalPrice: number;
  purchaseAmount: number;
  serviceCharge: number;
  gst: number;
  exchangeTime: string;
  tcs: number;
  isSupportedByOmFx?: boolean;
};

export type UserDetails = {
  userName: string;
  userEmail: string;
  userPhone: string;
  userCity: string;
  userState: string;
};

type CalcFormProps = {
  type: string;
  activeStep: number;
  handleStep: (step: number) => void;
  setLeadState: (state: LeadStore) => void;
  leadState: any;
  isAvailServices?: boolean;
};

const LeadForm = (props: CalcFormProps) => {
  const {
    type,
    setLeadState,
    leadState,
    isAvailServices,
    handleStep,
    activeStep,
  } = props;

  const { addNotification } = useNotificationStore();

  const [items, setItems] = useState<Item[]>(leadState.items);

  const [deleteItemIndex, setDeleteItemIndex] = useState<string>('');

  const { currencies: currencyOptions } = useCurrencyRates();
  const { calculatePrice } = useCalculatePrice();

  const { updateOrderAndCurrencyDetails, updateState } = useForexStore();
  const router = useRouter();

  const getExchangeRate = (currencyObject: Currency | undefined): number => {
    if (type === 'SELL' || type === 'UNLOAD')
      return currencyObject?.customerSellRate || 0;
    if (type === 'BUY' || type === 'RELOAD')
      return currencyObject?.customerBuyRate || 0;
    return currencyObject?.customerOutwardRemittanceRate || 0;
  };

  const [transactionDetails, setTransactionDetails] =
    useState<TransactionDetails>({
      transactionType: '',
      currency: currencyOptions[0]?.currencyCode,
      exchangeRate: getExchangeRate(currencyOptions[0]),
      totalPrice: 0,
      purchaseAmount: 0,
      serviceCharge: 0,
      gst: 0,
      exchangeTime: '',
      tcs: 0,
    });

  const getTransactionType = (transactionType: string) => {
    switch (transactionType) {
      case 'BUY':
        return buyOptionValues['Forex Cash'];
      case 'SELL':
        return 'SELL';
      case 'RELOAD':
        return 'CARD_RELOAD';
      case 'UNLOAD':
        return 'CARD_UNLOAD';
      case 'ABROAD':
        return 'OUTWARD_REMITTANCE';
      default:
        return '';
    }
  };

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors, dirtyFields },
    trigger,
    getValues,
  } = useForm<CalcFormType>({
    defaultValues: {
      exchangeRate: 0,
      forexAmount: '1',
      selectedCurrency: currencyOptions[0]?.currencyCode,
      inrAmount: currencyOptions[0]?.customerSellRate.toString(),
      isSupportedByOmFx: currencyOptions[0]?.isSupportedByOmFx,
      transactionType: getTransactionType(type),
      transactionProcess: TransactionProcess.Online,
    },
    mode: 'onChange',
    resolver: yupResolver(CalcFormSchema),
  });

  // const { isLoggedIn } = useAuth();
  const [addItemClick, setAddItemClick] = useState(false);

  useEffect(() => {
    const currencyItem = currencyOptions.filter(
      (item) => item.currencyCode === watch('selectedCurrency')
    );
    setValue('isSupportedByOmFx', currencyItem[0]?.isSupportedByOmFx);
  }, [watch('selectedCurrency')]);

  const deleteItem = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const [editItemDetails, setEditItemDetails] = useState<Item>({
    id: '',
    currency: '',
    exchangeRate: 0,
    inrAmount: '0',
    forexAmount: '0',
    transactionType: '',
    exchangeTime: '',
    isSupportedByOmFx: false,
  });

  const handleEditItem = (index: string) => {
    const editableItem = items.find((item) => item.id === index) as any;
    setEditItemDetails({
      id: editableItem.id,
      currency: editableItem.currency,
      exchangeRate: editableItem.exchangeRate,
      inrAmount: editableItem.inrAmount,
      forexAmount: editableItem.forexAmount,
      transactionType: editableItem.transactionType,
      exchangeTime: editableItem.exchangeTime,
      isSupportedByOmFx: editableItem.isSupportedByOmFx,
    });
  };

  const handleAddItem = (data: CalcFormType) => {
    const isCurrencyPresent = items.some(
      (item) => item.currency === data.selectedCurrency
    );
    if (!isCurrencyPresent) {
      const shapeItem: Item = {
        id: nanoid(),
        transactionType: data.transactionType,
        currency: data.selectedCurrency,
        inrAmount: data.inrAmount,
        forexAmount: data.forexAmount,
        exchangeRate: watch('exchangeRate'),
        exchangeTime: transactionDetails.exchangeTime,
        isSupportedByOmFx: data.isSupportedByOmFx,
      };
      setItems((prev) => [...prev, shapeItem]);
    } else {
      addNotification({
        title: 'Error',
        type: 'error',
        message: 'You can add only 1 item per currency',
      });
    }
  };

  const addTransactionDetails = (details: TransactionDetails) => {
    setTransactionDetails(details);
  };

  const dangerModalRef = useRef<HTMLButtonElement>(null);

  const getFormTitle = (transactionType: string) => {
    switch (transactionType) {
      case 'BUY':
        return 'Buy Forex';
      case 'SELL':
        return 'Sell Forex';
      case 'RELOAD':
        return 'Reload Forex Card';
      case 'ABROAD':
        return 'Send Money Abroad';
      case 'UNLOAD':
        return 'Unload Forex Card';
      default:
        return 'Currency Request';
    }
  };

  const getMoengagexTitle = (transactionType: string) => {
    switch (transactionType) {
      case 'BUY':
        return 'buy_forex';
      case 'SELL':
        return 'sell_forex';
      case 'RELOAD':
        return 'reload_forex_card';
      case 'ABROAD':
        return 'send_money_abroad';
      case 'UNLOAD':
        return 'unload_forex_card';
      default:
        return 'currency_request';
    }
  };

  const {
    register: editItemRegister,
    setValue: editItemSetValue,
    handleSubmit: editItemHandleSubmit,
    watch: editItemHandleWatch,
    formState: { errors: editItemFormErrors, dirtyFields: editItemDirtyFields },
    trigger: editItemTrigger,
    getValues: editItemGetValues,
  } = useForm<EditCalcFormType>({
    mode: 'onChange',
    resolver: yupResolver(CalcFormSchema),
  });

  useEffect(() => {
    editItemSetValue('isSupportedByOmFx', editItemDetails?.isSupportedByOmFx);
    editItemSetValue('exchangeRate', editItemDetails?.exchangeRate);
    editItemSetValue('forexAmount', editItemDetails?.forexAmount);
    editItemSetValue('inrAmount', editItemDetails?.inrAmount);
    editItemSetValue('selectedCurrency', editItemDetails?.currency);
    editItemSetValue('transactionType', editItemDetails?.transactionType);
    editItemSetValue('exchangeTime', editItemDetails?.exchangeTime);
    editItemSetValue('id', editItemDetails?.id);
  }, [editItemDetails]);

  useEffect(() => {
    if (currencyOptions.length) {
      setValue('selectedCurrency', currencyOptions[0]?.currencyCode);
      if (type === 'SELL' || type === 'UNLOAD') {
        setValue(
          'inrAmount',
          (
            currencyOptions[0].customerSellRate *
            parseFloat(getValues('forexAmount'))
          )
            .toFixed(2)
            .toString()
        );
        setValue('exchangeRate', currencyOptions[0]?.customerSellRate);
      } else if (type === 'BUY' || type === 'RELOAD') {
        setValue(
          'inrAmount',
          (
            currencyOptions[0].customerBuyRate *
            parseFloat(getValues('forexAmount'))
          )
            .toFixed(2)
            .toString()
        );
        setValue('exchangeRate', currencyOptions[0]?.customerBuyRate);
      } else {
        setValue(
          'inrAmount',
          (
            currencyOptions[0].customerOutwardRemittanceRate *
            parseFloat(getValues('forexAmount'))
          )
            .toFixed(2)
            .toString()
        );
        setValue(
          'exchangeRate',
          currencyOptions[0]?.customerOutwardRemittanceRate
        );
      }

      setTransactionDetails((state) => ({
        ...state,
        currency: currencyOptions[0]?.currencyCode,
        exchangeRate: getExchangeRate(currencyOptions[0]),
        exchangeTime: currencyOptions[0]?.exchangeRateDateTime,
        // exchangeTime: new Date().toISOString().replace('Z', '+05:30'),
        isSupportedByOmFx: currencyOptions[0]?.isSupportedByOmFx,
      }));
    } else {
      setValue('selectedCurrency', '');
      setValue('inrAmount', '');
      setTransactionDetails({
        transactionType: '',
        currency: '',
        exchangeRate: 0,
        totalPrice: 0,
        purchaseAmount: 0,
        serviceCharge: 0,
        gst: 0,
        exchangeTime: '',
        tcs: 0,
        isSupportedByOmFx: false,
      });
    }
  }, [currencyOptions, type]);

  const getPrice = async () => {
    const shapeItemsArray: TransactionItem[] = items.map((item) => {
      return {
        transactionType: item.transactionType,
        currency: item.currency,
        amount: item.forexAmount,
        exchangeRate: item.exchangeRate,
        exchangeTime: item.exchangeTime,
        isSupportedByOmFx: item.isSupportedByOmFx,
      };
    });
    const transactionItemsBody: CalculatePriceRequestBody = {
      transactionItems: shapeItemsArray,
    };
    if (items.length !== 0) {
      const { data } = await calculatePrice(transactionItemsBody);
      // const { data } = await axios.post(
      //   `${process.env.NEXT_PUBLIC_APP_API_URL}fx/leads/price`,
      //   transactionItemsBody
      // );
      addTransactionDetails({
        totalPrice: data.data.amountPayable,
        purchaseAmount: data.data.purchaseAmount,
        serviceCharge: data.data.serviceCharge,
        gst: data.data.gst,
        transactionType: transactionDetails.transactionType,
        currency: transactionDetails.currency,
        exchangeRate: watch('exchangeRate'),
        // exchangeTime: data.data.exchangeRateDateTime,
        exchangeTime: new Date().toISOString().replace('Z', '+05:30'),
        tcs: data.data.tcs,
      });
    } else {
      addTransactionDetails({
        totalPrice: 0,
        purchaseAmount: 0,
        serviceCharge: 0,
        gst: 0,
        transactionType: '',
        currency: currencyOptions[0]?.currencyCode,
        exchangeRate: currencyOptions[0]?.customerSellRate,
        exchangeTime: currencyOptions[0]?.exchangeRateDateTime,
        tcs: 0,
        // exchangeTime: new Date().toISOString().replace('Z', '+05:30'),
      });
    }
  };

  useEffect(() => {
    getPrice();
  }, [items]);

  useEffect(() => {
    if (type === 'BUY') {
      setValue('transactionType', buyOptionValues['Forex Cash']);
    }
  }, [type]);

  const getFxMoengageCurrencyList = () => {
    const moEngageItemsList: { [key: string]: string } = {};
    for (let i: number = 0; i < items.length; i += 1) {
      moEngageItemsList[`currency${(i + 1).toString()}`] = items[i].currency;
      moEngageItemsList[`quantity${(i + 1).toString()}`] =
        items[i].forexAmount.toString();
      moEngageItemsList[`inr${(i + 1).toString()}`] = (
        items[i].exchangeRate * parseFloat(items[i].forexAmount)
      ).toString();
    }
    return moEngageItemsList;
  };

  const {
    fxAddItemClicked,
    viewPriceBreakupClicked,
    mobileNumber,
    fxBookNowClicked,
  } = useMoengageStore((state) => {
    return {
      fxAddItemClicked: state.fxAddItemClicked,
      viewPriceBreakupClicked: state.viewPriceBreakupClicked,
      mobileNumber: state.mobileNumber,
      fxBookNowClicked: state.fxBookNowClicked,
    };
  });

  const onSubmit = (data: CalcFormType) => {
    if (items.length !== 5 && type != 'ABROAD') {
      handleAddItem(data);
    } else if (items.length < 1 && type == 'ABROAD') {
      handleAddItem(data);
    } else {
      addNotification({
        title: 'Error',
        type: 'error',
        message:
          type === 'ABROAD'
            ? 'You can add only 1 item'
            : 'You can add only 5 items',
      });
    }

    createMoengageEvent(fxAddItemClicked, {
      created_date: moment().format('YYYY-MM-DD HH:mm:ss'),
      order_amount: data.inrAmount,
      currency1: data.selectedCurrency,
      quantity1: data.forexAmount,
      inr1: data.inrAmount,
      service_type: getMoengagexTitle(type),
    });
  };
  useEffect(() => {
    if (
      type === 'ABROAD' ||
      type === 'BUY' ||
      type === 'SELL' ||
      type === 'UNLOAD' ||
      type === 'RELOAD'
    ) {
      const transactionItems = items.map((item: any) => ({
        transactionType: item.transactionType,
        currency: item.currency,
        // amount: parseInt(item.forexAmount, 10),
        amount: item.forexAmount,
        exchangeRate: item.exchangeRate,
        exchangeTime: item.exchangeTime,
        isSupportedByOmFx: item.isSupportedByOmFx,
      }));
      updateOrderAndCurrencyDetails({
        transactionItems,
        transactionProcess: leadState.transactionProcess,
      });
      if (type === 'ABROAD') {
        updateState({
          transactionType: FlowType.SendMoneyAbroad,
        });
      } else if (type === 'UNLOAD') {
        updateState({
          transactionType: FlowType.UnloadCard,
        });
      } else if (type === 'RELOAD') {
        updateState({
          transactionType: FlowType.ReloadCard,
        });
      } else {
        updateState({
          transactionType: type,
        });
      }
      if (transactionItems.length != 0)
        localStorage.setItem('transac', JSON.stringify(transactionItems));
      // router.push('/forex/v1/send-money-abroad');
    }
  }, [type, leadState, items]);

  const onBookNow = () => {
    setAddItemClick(false);
    if (transactionDetails.totalPrice <= 0 && items.length >= 1) {
      addNotification({
        title: 'Error',
        type: 'error',
        message:
          'Unable to proceed due to negative total amount receivable. You will need to add more money to cover the total amount due before proceeding',
      });
    }
    if (items.length >= 1 && transactionDetails.totalPrice > 0) {
      setTransactionDetails({
        transactionType: getValues('transactionType'),
        currency: getValues('selectedCurrency'),
        exchangeRate: getValues('exchangeRate'),
        totalPrice: transactionDetails.totalPrice,
        purchaseAmount: transactionDetails.purchaseAmount,
        serviceCharge: transactionDetails.serviceCharge,
        gst: transactionDetails.gst,
        exchangeTime: transactionDetails.exchangeTime,
        tcs: transactionDetails.tcs,
        isSupportedByOmFx: transactionDetails.isSupportedByOmFx,
      });
      setLeadState({
        ...leadState,
        items,
        transactionProcess: getValues('transactionProcess'),
      });
      const isSupporteByMfl = items.every(
        (item: any) => item.isSupportedByOmFx === false
      );
      if (!isSupporteByMfl) {
        switch (type) {
          case 'ABROAD':
            router.push(routes.forexSendMoneyAbroad);
            break;
          case 'BUY':
            router.push(routes.forexBuy);
            break;
          case 'SELL':
            router.push(routes.forexSell);
            break;
          case 'UNLOAD':
            router.push(routes.forexUnloadCard);
            break;
          case 'RELOAD':
            router.push(routes.forexReloadCard);
            break;
          default:
            router.push(routes.forexCurrencyRequest);
            handleStep(activeStep + 1);
            break;
        }
      } else {
        // updateState({
        //   transactionType: FlowType.CurrencyRequest,
        // });
        router.push(routes.forexCurrencyRequest);
      }
    }
    const eventList = getFxMoengageCurrencyList();
    createMoengageEvent(fxBookNowClicked, {
      created_date: moment().format('YYYY-MM-DD HH:mm:ss'),
      mobile_number: mobileNumber,
      ...eventList,
      service_type: 'send_money_abroad',
    });
    // if (items.length === 0 && isLoggedIn) {
    //   dangerModalRef.current?.click();
    // }
  };

  const optimizeBookNow = async () => {
    await trigger();
    const noErrors = Object.keys(errors).length === 0;
    if (noErrors) {
      onSubmit({
        forexAmount: watch('forexAmount'),
        inrAmount: watch('inrAmount'),
        exchangeRate: watch('exchangeRate'),
        selectedCurrency: watch('selectedCurrency'),
        transactionType: watch('transactionType'),
        exchangeTime: watch('exchangeTime'),
        transactionProcess: watch('transactionProcess'),
        isSupportedByOmFx: watch('isSupportedByOmFx'),
      });
      setLeadState({
        ...leadState,
        items,
        transactionProcess: getValues('transactionProcess'),
      });
      setAddItemClick(true);
    }
  };

  useEffect(() => {
    if (transactionDetails.totalPrice !== 0 && addItemClick) {
      const transactionItems = leadState.items.map((item: any) => ({
        transactionType: item.transactionType,
        currency: item.currency,
        // amount: parseInt(item.forexAmount, 10),
        amount: item.forexAmount,
        exchangeRate: item.exchangeRate,
        exchangeTime: item.exchangeTime,
        isSupportedByOmFx: item.isSupportedByOmFx,
      }));
      updateOrderAndCurrencyDetails({
        transactionItems,
        transactionProcess: leadState.transactionProcess,
      });
      if (type === 'ABROAD') {
        updateState({
          transactionType: FlowType.SendMoneyAbroad,
        });
      } else if (type === 'UNLOAD') {
        updateState({
          transactionType: FlowType.UnloadCard,
        });
      } else if (type === 'RELOAD') {
        updateState({
          transactionType: FlowType.ReloadCard,
        });
      } else {
        updateState({
          transactionType: type,
        });
      }
      if (transactionItems.length != 0)
        localStorage.setItem('transac', JSON.stringify(transactionItems));
      const isSupporteByMfl = items.every(
        (item: any) => item.isSupportedByOmFx === true
      );
      if (isSupporteByMfl) {
        switch (type) {
          case 'ABROAD':
            router.push(routes.forexSendMoneyAbroad);
            break;
          case 'BUY':
            router.push(routes.forexBuy);
            break;
          case 'SELL':
            router.push(routes.forexSell);
            break;
          case 'UNLOAD':
            router.push(routes.forexUnloadCard);
            break;
          case 'RELOAD':
            router.push(routes.forexReloadCard);
            break;
          default:
            router.push(routes.forexCurrencyRequest);
            handleStep(activeStep + 1);
            break;
        }
      } else {
        router.push(routes.forexCurrencyRequest);
      }
    }
  }, [items, transactionDetails.totalPrice, addItemClick]);

  const closeEditModalRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setValue(
      'inrAmount',
      (parseFloat(getValues('forexAmount')) * watch('exchangeRate')).toFixed(2)
    );
  }, [watch('exchangeRate')]);

  useEffect(() => {
    editItemSetValue(
      'inrAmount',
      (
        parseFloat(editItemGetValues('forexAmount')) *
        editItemDetails.exchangeRate
      ).toFixed(2)
    );
  }, [editItemDetails.exchangeRate]);

  useEffect(() => {
    if (
      watch('inrAmount') === '0' ||
      watch('inrAmount') === '' ||
      watch('forexAmount') === '0' ||
      watch('forexAmount') === ''
    ) {
      setValue('forexAmount', '1');
      setValue(
        'inrAmount',
        (watch('exchangeRate') * parseFloat(watch('forexAmount')))
          .toFixed(2)
          .toString()
      );
      trigger('forexAmount');
      trigger('inrAmount');
    }
  }, [watch('selectedCurrency')]);

  useEffect(() => {
    if (
      editItemHandleWatch('inrAmount') === '0' ||
      editItemHandleWatch('inrAmount') === '' ||
      editItemHandleWatch('forexAmount') === '0' ||
      editItemHandleWatch('forexAmount') === ''
    ) {
      editItemSetValue('forexAmount', '1');
      editItemSetValue(
        'inrAmount',
        (
          editItemHandleWatch('exchangeRate') *
          parseFloat(editItemHandleWatch('forexAmount'))
        )
          .toFixed(2)
          .toString()
      );
      editItemTrigger('forexAmount');
      editItemTrigger('inrAmount');
    }
  }, [editItemHandleWatch('selectedCurrency')]);

  const getBuyOptionName = (optionValue: string) => {
    return Object.keys(buyOptionValues).find(
      (key) => buyOptionValues[key] === optionValue
    );
  };

  const onEditFormSubmit = (data: EditCalcFormType) => {
    const editItemIndex = items.findIndex((item) => item.id === data.id);
    const isCurrencyPresentInOtherItems = items.some(
      (item, index) =>
        index !== editItemIndex && item.currency === data.selectedCurrency
    );
    if (editItemIndex !== -1 && !isCurrencyPresentInOtherItems) {
      const updatedItems = [...items];
      updatedItems[editItemIndex] = {
        currency: data.selectedCurrency,
        exchangeRate: data.exchangeRate,
        forexAmount: data.forexAmount,
        inrAmount: data.inrAmount,
        transactionType: data.transactionType,
        exchangeTime: data.exchangeTime,
        id: updatedItems[editItemIndex].id,
        isSupportedByOmFx: data.isSupportedByOmFx,
      };
      setItems(updatedItems);
      closeEditModalRef.current?.click();
    } else {
      addNotification({
        title: 'Error',
        type: 'error',
        message: 'You can add only 1 item per currency',
      });
    }
  };

  return (
    <>
      <div className="callback-card">
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <div className="stepper--content active">
            <p className="login__title  text--bold fx-black-color text-left">
              {getFormTitle(type)}
            </p>
            {isAvailServices && (
              <div className="service__details pb-2">
                <div className="service__item">
                  <input
                    type="radio"
                    id="serviceOnline"
                    value={TransactionProcess.Online}
                    {...register('transactionProcess')}
                  />
                  <label
                    htmlFor="serviceOnline"
                    className="text--black text--regular service__title"
                  >
                    Get Service Online
                  </label>
                </div>
                <div className="service__item">
                  <input
                    type="radio"
                    id="serviceBranch"
                    value={TransactionProcess.Branch}
                    {...register('transactionProcess')}
                  />
                  <label
                    htmlFor="serviceBranch"
                    className="text--black text--regular service__title"
                  >
                    Get Service At Branch
                  </label>
                </div>
              </div>
            )}
            {type === 'BUY' && (
              <div className="input-container">
                <BasicDropdown
                  label="Product"
                  options={buyOptions}
                  placeholder="Select Type"
                  value={getBuyOptionName(watch('transactionType')) || ''}
                  onChange={(transType) => {
                    setValue('transactionType', buyOptionValues[transType], {
                      shouldDirty: true,
                    });
                    if (dirtyFields.transactionType) {
                      trigger('transactionType');
                    }
                  }}
                  errorMessage={errors.transactionType?.message || ''}
                />
              </div>
            )}
            <div className="input-container">
              <CurrencyDropdown
                currencyOptions={currencyOptions}
                placeholder="Select Currency"
                value={watch('selectedCurrency')}
                onSelect={(currencyCode) => {
                  setValue('exchangeRate', getExchangeRate(currencyCode));

                  setValue('isSupportedByOmFx', currencyCode.isSupportedByOmFx);
                  setTransactionDetails((state) => ({
                    ...state,
                    exchangeRate: getExchangeRate(currencyCode),
                    // exchangeTime: new Date()
                    //   .toISOString()
                    //   .replace('Z', '+05:30'),
                  }));
                }}
                onChange={(currencyCode) => {
                  setValue('selectedCurrency', currencyCode, {
                    shouldDirty: true,
                  });
                  if (dirtyFields.selectedCurrency) {
                    trigger('selectedCurrency');
                  }
                }}
                errorMessage={errors.selectedCurrency?.message || ''}
              />
            </div>
            <div className="input-container">
              <div className="form-currency-container">
                <p className="form-currency">
                  1 {watch('selectedCurrency')} ={' '}
                  {watch('exchangeRate').toLocaleString('en-IN', {
                    maximumFractionDigits: 2,
                  })}
                </p>
                <p className="form-gurantee-pill">
                  <span className="form-gurantee-text">
                    Guaranteed rate for 24 hrs
                  </span>
                </p>
              </div>
            </div>
            <div className="input-container">
              <div className="form-cuurency-exchange">
                <FieldWrapper
                  label={'Forex Currency'}
                  error={errors.forexAmount}
                >
                  <div className="input-form">
                    <input
                      type="text"
                      placeholder={`1 ${getValues('selectedCurrency')}`}
                      {...register('forexAmount', {
                        onChange: () => {
                          if (
                            getValues('forexAmount') === '0' ||
                            getValues('forexAmount') === ''
                          ) {
                            setValue('inrAmount', '0');
                          } else {
                            setValue(
                              'inrAmount',
                              (
                                parseFloat(getValues('forexAmount')) *
                                watch('exchangeRate')
                              ).toFixed(2)
                            );
                          }
                        },
                      })}
                    />
                  </div>
                </FieldWrapper>
                <FieldWrapper label={'INR Currency'} error={errors.inrAmount}>
                  <div className="input-form">
                    <input
                      type="text"
                      placeholder={`${watch('exchangeRate')} INR`}
                      {...register('inrAmount', {
                        onChange: () => {
                          if (
                            getValues('inrAmount') === '0' ||
                            getValues('inrAmount') === ''
                          ) {
                            setValue('forexAmount', '0');
                          } else {
                            setValue(
                              'forexAmount',
                              (
                                parseFloat(getValues('inrAmount')) /
                                watch('exchangeRate')
                              ).toFixed(2)
                            );
                          }
                        },
                      })}
                    />
                  </div>
                </FieldWrapper>
              </div>
            </div>
            <button
              className="input-container add-item-pill add-border cursor-pointer"
              type="submit"
              id="forex_lead-form_add-item"
            >
              <Image
                style={{ color: undefined }}
                src={AddItemIcon}
                alt="add-item-icon"
              />
              <span className="form-add-item">Add Item</span>
            </button>
            <div className="form-card-container">
              {items.map((item, id) => (
                <div className="form-card" key={id}>
                  {type === 'BUY' && (
                    <div className="form-card-item">
                      <h1 className="form-card-heading">Product</h1>
                      <h1 className="form-card-value">
                        {item.transactionType === 'BUY_CARD'
                          ? 'Forex Card'
                          : 'Forex Cash'}
                      </h1>
                    </div>
                  )}
                  <div className="form-card-item">
                    <h1 className="form-card-heading">Currency</h1>
                    <h1 className="form-card-value">
                      <CurrencyIcon
                        currencyCode={item.currency}
                        isFullFormRequired={false}
                      />
                    </h1>
                  </div>
                  <div className="form-card-item">
                    <h1 className="form-card-heading">Forex Amount</h1>
                    <h1 className="form-card-value">
                      {parseFloat(item.forexAmount).toLocaleString('en-IN', {
                        maximumFractionDigits: 2,
                      })}
                    </h1>
                  </div>
                  <div className="form-card-item">
                    <h1 className="form-card-heading">INR Amount</h1>
                    <h1 className="form-card-value">
                      ₹
                      {parseFloat(item.inrAmount).toLocaleString('en-IN', {
                        maximumFractionDigits: 2,
                      })}
                    </h1>
                  </div>
                  <div className="form-action-container">
                    <button
                      type="button"
                      data-target="#editModal"
                      data-toggle="modal"
                      onClick={() => handleEditItem(item.id)}
                      id="forex_edit_item"
                    >
                      <Image
                        style={{ color: undefined }}
                        src={EditIcon}
                        alt="edit icon"
                      />
                    </button>
                    <button
                      type="button"
                      data-target="#deleteModal"
                      data-toggle="modal"
                      onClick={() => {
                        setDeleteItemIndex(item.id);
                      }}
                      id="forex_delete_item"
                    >
                      <Image
                        style={{ color: undefined }}
                        src={DeleteIcon}
                        alt="delete-icon"
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="input-container form-price-container">
              <p className="form-price">
                Order Amount{' '}
                <span className="price">
                  ₹{' '}
                  {transactionDetails.totalPrice.toLocaleString('en-IN', {
                    maximumFractionDigits: 2,
                  })}
                </span>
              </p>
              <button
                className={
                  items.length > 0
                    ? 'form-breakup form-breakup-active'
                    : 'form-breakup'
                }
                disabled={items.length === 0}
                type="button"
                data-target="#priceModal"
                data-toggle="modal"
                id="forex_price_breakup"
                onClick={() => {
                  const eventList = getFxMoengageCurrencyList();
                  createMoengageEvent(viewPriceBreakupClicked, {
                    created_date: moment().format('YYYY-MM-DD HH:mm:ss'),
                    mobile_number: mobileNumber,
                    ...eventList,
                    service_type: getMoengagexTitle(type),
                  });
                }}
              >
                View Price Breakup
              </button>
            </div>
            <div className="action-container">
              <button
                type="button"
                className={`custom__button custom__button--dark text--bold w-100 blue fx-btn-height position-relative`}
                onClick={() =>
                  items.length !== 0 ? onBookNow() : optimizeBookNow()
                }
                id="forex_book_now"
              >
                Book Now
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* View Price Modal */}
      <div
        className="modal loanDetails__modal repayModal visitSchedule login pledge loginFirstModal otpModal"
        id="priceModal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="priceModalLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div
          className="modal-dialog modal-dialog-centered modal-static"
          role="document"
        >
          <div className="modal-content">
            <div className="modal__title">
              <p className="fx-black-color text--bold title m-0">
                Price Breakup
              </p>
              <button data-dismiss="modal">
                <Image
                  style={{ color: undefined }}
                  className="img-fluid"
                  src={CloseIcon}
                  alt="close icon"
                />
              </button>
            </div>
            <div className={`stepper--content active`}>
              <div className="price-modal-body">
                <div className="price-modal-item">
                  <h1 className="price-modal-heading m-0">
                    {type === 'SELL' || type === 'UNLOAD'
                      ? 'Total Amount'
                      : 'Purchase Amount'}
                  </h1>
                  <p className="price-modal-value m-0">
                    ₹
                    {transactionDetails.purchaseAmount.toLocaleString('en-IN', {
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
                <div className="price-modal-item">
                  <h1 className="price-modal-heading m-0">Service Charge</h1>
                  <p className="price-modal-value m-0">
                    {type === 'SELL' || type === 'UNLOAD' ? '-' : '+'} ₹
                    {transactionDetails.serviceCharge.toLocaleString('en-IN', {
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
                <div
                  className={`price-modal-item ${
                    transactionDetails.tcs === 0 && 'price-modal-add-border'
                  }`}
                >
                  <h1 className="price-modal-heading m-0">GST</h1>
                  <p className="price-modal-value m-0">
                    {type === 'SELL' || type === 'UNLOAD' ? '-' : '+'} ₹
                    {transactionDetails.gst.toLocaleString('en-IN', {
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
                {transactionDetails.tcs !== 0 && (
                  <div
                    className={`price-modal-item ${
                      transactionDetails.tcs != 0 && 'price-modal-add-border'
                    }`}
                  >
                    <h1 className="price-modal-heading m-0">TCS</h1>
                    <h2 className="price-modal-value m-0">
                      {type === 'SELL' || type === 'UNLOAD' ? '-' : '+'} ₹
                      {transactionDetails.tcs.toLocaleString('en-IN', {
                        maximumFractionDigits: 2,
                      })}
                    </h2>
                  </div>
                )}
                <div className="price-modal-item">
                  <h1 className="price-modal-order m-0">Order Amount</h1>
                  <p className="price-modal-value m-0">
                    ₹{' '}
                    {transactionDetails.totalPrice.toLocaleString('en-IN', {
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <div
        className="modal loanDetails__modal repayModal visitSchedule login pledge loginFirstModal otpModal"
        id="deleteModal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="deleteModalLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div
          className="modal-dialog modal-dialog-centered modal-static"
          role="document"
        >
          <div className="modal-content">
            <div className="modal__title m-0">
              <button data-dismiss="modal"></button>
            </div>
            <div className={`stepper--content active`}>
              <div className="price-modal-body mt-0">
                <Image
                  style={{ color: undefined }}
                  src={WarningIcon}
                  alt="warning-icon"
                />
                <p className="price-modal-value">
                  Are you sure, you want to delete this item?
                </p>
                <div className="delete-modal-item">
                  <button
                    className="delete-modal-yes-button"
                    type="button"
                    data-dismiss="modal"
                    onClick={() => {
                      deleteItem(deleteItemIndex);
                      setDeleteItemIndex('');
                    }}
                  >
                    Yes
                  </button>
                  <button
                    className="delete-modal-no-button"
                    data-dismiss="modal"
                    onClick={() => {
                      setDeleteItemIndex('');
                    }}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        data-target="#dangerModal"
        data-toggle="modal"
        ref={dangerModalRef}
        className="hidden"
      ></button>

      {/* Edit Modal */}
      <div
        className="modal loanDetails__modal repayModal visitSchedule login pledge loginFirstModal otpModal"
        id="editModal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="editModalLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div
          className="modal-dialog modal-dialog-centered modal-static"
          role="document"
        >
          <div className="modal-content">
            <div className="modal__title">
              <p className="fx-black-color text--bold title m-0">
                Edit item details
              </p>
              <button data-dismiss="modal" ref={closeEditModalRef}>
                <Image
                  style={{ color: undefined }}
                  className="img-fluid"
                  src={CloseIcon}
                  alt="close icon"
                />
              </button>
            </div>
            <div className={`stepper--content active`}>
              <div className="price-modal-body">
                <form
                  autoComplete="off"
                  onSubmit={editItemHandleSubmit(onEditFormSubmit)}
                >
                  {type === 'BUY' && (
                    <div className="input-container">
                      <BasicDropdown
                        label="Product"
                        options={buyOptions}
                        placeholder="Select Type"
                        value={
                          getBuyOptionName(
                            editItemHandleWatch('transactionType')
                          ) || ''
                        }
                        onChange={(transType) => {
                          editItemSetValue(
                            'transactionType',
                            buyOptionValues[transType],
                            {
                              shouldDirty: true,
                            }
                          );
                          if (editItemDirtyFields.transactionType) {
                            editItemTrigger('transactionType');
                          }
                        }}
                        errorMessage={
                          editItemFormErrors.transactionType?.message || ''
                        }
                      />
                    </div>
                  )}
                  <div className="input-container">
                    <CurrencyDropdown
                      currencyOptions={currencyOptions}
                      placeholder="Select Currency"
                      value={editItemHandleWatch('selectedCurrency')}
                      onSelect={(editCurrencyCode) => {
                        setEditItemDetails((state) => ({
                          ...state,
                          exchangeRate: editCurrencyCode.customerSellRate,
                          currency: editCurrencyCode.currencyCode,
                        }));
                      }}
                      onChange={(editCurrencyCode) => {
                        editItemSetValue('selectedCurrency', editCurrencyCode, {
                          shouldDirty: true,
                        });
                        if (editItemDirtyFields.selectedCurrency) {
                          editItemTrigger('selectedCurrency');
                        }
                      }}
                      errorMessage={
                        editItemFormErrors.selectedCurrency?.message || ''
                      }
                    />
                  </div>
                  <div className="input-container">
                    <FieldWrapper
                      label={'Forex Currency'}
                      error={editItemFormErrors.forexAmount}
                    >
                      <div className="input-form">
                        <input
                          data-hj-allow
                          type="text"
                          placeholder={`1 ${editItemGetValues(
                            'selectedCurrency'
                          )}`}
                          {...editItemRegister('forexAmount', {
                            // onChange: () =>
                            // editItemSetValue(
                            //   'inrAmount',
                            //   (
                            //     parseFloat(editItemGetValues('forexAmount')) *
                            //     editItemDetails.exchangeRate
                            //   ).toFixed(2)
                            // ),
                            onChange: () => {
                              if (
                                editItemGetValues('forexAmount') === '0' ||
                                editItemGetValues('forexAmount') === ''
                              ) {
                                editItemSetValue('inrAmount', '0');
                              } else {
                                editItemSetValue(
                                  'inrAmount',
                                  (
                                    parseFloat(
                                      editItemGetValues('forexAmount')
                                    ) * editItemHandleWatch('exchangeRate')
                                  ).toFixed(2)
                                );
                              }
                            },
                          })}
                        />
                      </div>
                    </FieldWrapper>
                  </div>
                  <div className="input-container">
                    <div className="form-currency-container">
                      <p className="form-currency">
                        1 {editItemHandleWatch('selectedCurrency')} ={' '}
                        {editItemDetails.exchangeRate.toLocaleString('en-IN', {
                          maximumFractionDigits: 2,
                        })}
                      </p>
                      <p className="form-gurantee-pill">
                        <span className="form-gurantee-text">
                          Guaranteed rate for 24 hrs
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="input-container">
                    <FieldWrapper
                      label={'INR Currency'}
                      error={editItemFormErrors.inrAmount as any}
                    >
                      <div className="input-form">
                        <input
                          data-hj-allow
                          type="text"
                          placeholder={`${editItemDetails.exchangeRate} INR`}
                          {...editItemRegister('inrAmount', {
                            // onChange: () =>
                            //   editItemSetValue(
                            //     'forexAmount',
                            //     (
                            //       parseFloat(editItemGetValues('inrAmount')) *
                            //       editItemDetails.exchangeRate
                            //     ).toFixed(2)
                            //   ),
                            onChange: () => {
                              if (
                                editItemGetValues('inrAmount') === '0' ||
                                editItemGetValues('inrAmount') === ''
                              ) {
                                editItemSetValue('forexAmount', '0');
                              } else {
                                editItemSetValue(
                                  'forexAmount',
                                  (
                                    parseFloat(editItemGetValues('inrAmount')) /
                                    editItemHandleWatch('exchangeRate')
                                  ).toFixed(2)
                                );
                              }
                            },
                          })}
                          inputMode="numeric"
                        />
                      </div>
                    </FieldWrapper>
                  </div>
                  <div className="delete-modal-item">
                    <button
                      className="delete-modal-yes-button"
                      type="button"
                      data-dismiss="modal"
                      onClick={() => {
                        setEditItemDetails({
                          id: '',
                          currency: '',
                          exchangeRate: 0,
                          inrAmount: '0',
                          forexAmount: '0',
                          transactionType: '',
                          exchangeTime: '',
                          isSupportedByOmFx: false,
                        });
                      }}
                    >
                      Cancel
                    </button>
                    <button className="delete-modal-no-button" type="submit">
                      Save
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Danger Modal */}
      <div
        className="modal loanDetails__modal repayModal visitSchedule login pledge loginFirstModal otpModal"
        id="dangerModal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="dangerModalLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div
          className="modal-dialog modal-dialog-centered modal-static"
          role="document"
        >
          <div className="modal-content">
            <div className="modal__title m-0">
              <button data-dismiss="modal"></button>
            </div>
            <div className={`stepper--content active`}>
              <div className="price-modal-body mt-0">
                <Image
                  style={{ color: undefined }}
                  src={DangerIcon}
                  alt="danger-icon"
                />
                <p className="price-modal-value text-center">
                  Sorry, the order cannot be empty. Please select currency and
                  add item to Book Order
                </p>
                <div className="delete-modal-item">
                  <button
                    className="delete-modal-no-button"
                    data-dismiss="modal"
                  >
                    Okay
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadForm;
